/**
 * Organisation lifecycle — archive (reversible) and the policy that guards
 * removal.
 *
 * Two different things get called "deleting a school" and they are not the
 * same:
 *
 *   • ARCHIVE  — snapshot everything into `organization_archives/{archiveId}`
 *                and flag the org `archived: true`. Nothing is destroyed; the
 *                org stops appearing in normal lists. Reversible.
 *   • PURGE    — permanently delete the org and every record that names it:
 *                people and their logins, vehicles, trips, children, Families
 *                and Zana data, and any snapshot. Only payment records stay.
 *                Irreversible. The delete runs on the server (see PURGE
 *                below); this file asks for it.
 *
 * Both live here so the dashboard and the console share ONE copy of each. Two
 * copies of a destructive routine drift, and the copy that drifts is the one
 * that orphans real data — which is precisely how Known bug 24 happened, and
 * then how the 2026-09-30 Families leftovers happened.
 */
import {
  collection, doc, getDoc, getDocs, query, setDoc, updateDoc, where, Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import { purgeOrgOnServer } from './paymentsApi';

/** Statuses that mean money is still moving. Removal is refused on these. */
const BILLING_ACTIVE = new Set(['Active', 'Trial']);

export interface RemovalGuard {
  /** False when the org may not be archived or purged right now. */
  allowed: boolean;
  /** Shown to the operator when `allowed` is false. */
  reason?: string;
}

/**
 * Whether an org may be removed at all.
 *
 * A live plan is refused on purpose: archiving or purging underneath an active
 * subscription leaves Paystack billing a school that no longer exists, and the
 * operator finds out when the next charge lands. Cancel first, then remove.
 */
export function guardRemoval(
  status: string,
  plan?: string,
  billing?: { hasActiveSubscription?: boolean | null },
): RemovalGuard {
  /* 2026-09-30: "How do I delete those two while they are on trial?" A Trial
     or Active LABEL is not a live charge. What the guard protects against is
     Paystack billing a school that no longer exists — and that needs a
     subscription. An org whose document says hasActiveSubscription: false
     (a pilot that never took a card, an expired trial) has nothing to cancel,
     so it may go. Only when the field is unknown does the label decide. */
  if (billing && billing.hasActiveSubscription === false) return { allowed: true };
  if (BILLING_ACTIVE.has(status)) {
    return {
      allowed: false,
      reason: `This organisation is ${status.toLowerCase()}${plan ? ` on the ${plan} plan` : ''}. ` +
        'Cancel the subscription first — archiving or deleting while billing is live leaves Paystack ' +
        'charging a school that no longer exists.',
    };
  }
  return { allowed: true };
}

/**
 * Snapshot an organisation and everything attached to it, then flag the org
 * archived. Returns the archive id so the operator can find it again.
 *
 * Reversible by design: nothing is deleted, so a school archived by mistake can
 * be restored from `organization_archives/{archiveId}`.
 */
export async function archiveOrganization(orgCode: string, actorUid?: string): Promise<string> {
  const archivedAt = Timestamp.now();
  const archiveId = `archive_${orgCode}_${archivedAt.toMillis()}`;

  const archiveData: Record<string, unknown> = {
    organizationCode: orgCode,
    archivedAt,
    archivedBy: actorUid || 'pear-console',
    version: '1.0',
  };

  const orgDoc = await getDoc(doc(db, 'organizations', orgCode));
  if (orgDoc.exists()) archiveData.organization = orgDoc.data();

  // Each collection is keyed differently by history: users carry `schoolCode`,
  // everything else `organizationCode`.
  const snap = async (col: string, field: string) =>
    (await getDocs(query(collection(db, col), where(field, '==', orgCode)))).docs
      .map((d) => ({ id: d.id, data: d.data() }));

  archiveData.users = await snap('users', 'schoolCode');
  archiveData.classes = await snap('classes', 'organizationCode');
  archiveData.conversations = await snap('conversations', 'organizationCode');
  archiveData.posts = await snap('posts', 'organizationCode');
  archiveData.notifications = await snap('notifications', 'organizationCode');

  await setDoc(doc(db, 'organization_archives', archiveId), archiveData);

  if (orgDoc.exists()) {
    await updateDoc(doc(db, 'organizations', orgCode), {
      archived: true,
      archivedAt,
      archiveId,
      updatedAt: Timestamp.now(),
    });
  }

  return archiveId;
}

/** Undo an archive: clear the flags so the org returns to normal listings. */
export async function unarchiveOrganization(orgCode: string): Promise<void> {
  await updateDoc(doc(db, 'organizations', orgCode), {
    archived: false,
    unarchivedAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
  });
}

/* ────────────────────────────────────────────────────────────────────────────
   PURGE — irreversible.

   2026-09-30: the delete itself now runs on the SERVER. It used to run here, in
   the browser, from this file's own list of collections, and that list drifted:
   it never learned about children, Trasse Families, Zana's stores, parent
   memberships, guest logins or trail chunks, and it could not have reached the
   Families one-trial claims at all — no client may touch them. Gabriel deleted
   three households and their addresses were told "already used a free trial —
   sign in instead" about logins that no longer existed.

   The server sweeps from the one list the post-cancel wipe also uses
   (pear-payments-backend/src/orgWipe.js → purgeOrgFromConsole), logins
   included.

   No snapshot. Delete used to save one first and keep it; asked what Delete
   should keep, Gabriel: "not payment records, but every other thing." So the
   server removes the org's snapshots too, and only payment records stay.
   ARCHIVE above is the reversible choice — it is a separate button, and the
   console says so before anyone confirms a delete.
   ──────────────────────────────────────────────────────────────────────────── */

export interface PurgeResult {
  /** What the server removed, per collection. */
  counts: Record<string, number>;
  /** Logins the server could not delete — those people can still sign in to an empty app. */
  authFailed: number;
}

/**
 * Permanently delete an organisation and every record that names it, except
 * its payment records.
 *
 * Throws when the server did not delete — and says so. Nothing is touched from
 * this side, so a failure leaves the organisation exactly as it was.
 */
export async function purgeOrganization(orgCode: string, orgDocId?: string): Promise<PurgeResult> {
  const res = await purgeOrgOnServer(orgCode, orgDocId);
  if (!res.ok) {
    throw new Error(
      `Nothing was deleted: ${res.message || 'the server did not answer'}. Try again in a minute.`,
    );
  }
  return { counts: res.counts || {}, authFailed: res.authFailed || 0 };
}
